import { useTranslations } from '@lang';
import type { RoomExtras } from '@features/rooms';
import { useRoomExtrasLabels } from '@features/RoomRateConfigurationWizard/FormSteps/ExtrasStepV2/useRoomExtrasLabels';
import {
  ExtraFormEntry,
  buildExtrasItems,
  editResultPatch,
  guestSelectionPatch,
} from '@features/RoomRateConfigurationWizard/FormSteps/ExtrasStepV2/utils';
import { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import type { ExtrasEditResult } from 'voyager';
import { useCurrentStep, usePackageConfiguration } from '../../contexts';
import { fillMissingEntries, toRoomExtraEntries } from '../../helpers/extrasEntries';
import { OvernightPackageRoomExtra, PackagesFormValues } from '../../types';
import { PackageExtrasPage } from '../PackageExtrasPage';

const toFormEntry = (entry: OvernightPackageRoomExtra | undefined): ExtraFormEntry | undefined =>
  entry && {
    isSelected: entry.isSelected,
    quantity: entry.quantity,
    optionSelections: entry.optionSelections,
    guestSelections: entry.guestSelections,
  };

function byIndex<T>(
  entries: (ExtraFormEntry | undefined)[],
  pick: (entry: ExtraFormEntry | undefined) => T
) {
  return Object.fromEntries(entries.map((entry, index) => [index, pick(entry)])) as Record<
    number,
    T
  >;
}

export const RoomExtrasStepV2 = () => {
  const { language, availableExtras } = usePackageConfiguration();
  const { t } = useTranslations();
  const { packageTotalPrice } = useCurrentStep();
  const { watch, setValue, getValues } = useFormContext<PackagesFormValues>();

  const extras = useMemo(() => (availableExtras ?? []) as RoomExtras, [availableExtras]);
  const formExtras = watch('extras');
  const guestCount = (watch('guests') ?? []).length;
  const entries = useMemo(() => (formExtras ?? []).map(toFormEntry), [formExtras]);
  const roomLabels = useRoomExtrasLabels(language);
  const labels = useMemo(
    () => ({
      ...roomLabels,
      title: t('package.extrasTitle'),
      subtitle: t('package.extrasSubtitle'),
    }),
    [roomLabels, t]
  );

  useEffect(() => {
    if (extras.length && !(getValues('extras') ?? []).length) {
      setValue('extras', toRoomExtraEntries(extras));
    }
  }, [extras, getValues, setValue]);

  const items = useMemo(
    () =>
      buildExtrasItems({
        extras,
        entries,
        optionSelections: byIndex(entries, (entry) => entry?.optionSelections),
        guestSelections: byIndex(entries, (entry) => entry?.guestSelections),
        language,
        fallbackCurrencyCode: packageTotalPrice.currencyCode,
      }),
    [extras, entries, language, packageTotalPrice.currencyCode]
  );

  const setEntry = (index: number, patch: Partial<ExtraFormEntry>) => {
    const updated = fillMissingEntries(toRoomExtraEntries(extras), getValues('extras') ?? []);
    updated[index] = { ...updated[index], ...patch };
    setValue('extras', updated, { shouldDirty: true });
  };

  const handleAdd = (itemId: string) => setEntry(Number(itemId), { isSelected: true });
  const handleRemove = (itemId: string) => setEntry(Number(itemId), { isSelected: false });
  const handleConfirmGuestSelection = (itemId: string, selectedGuestIds: string[]) =>
    setEntry(Number(itemId), guestSelectionPatch(selectedGuestIds));
  const handleConfirmSelection = (itemId: string, result: ExtrasEditResult) => {
    const index = Number(itemId);
    setEntry(index, editResultPatch(extras[index], result, guestCount));
  };

  return (
    <PackageExtrasPage
      items={items}
      onAdd={handleAdd}
      onRemove={handleRemove}
      onConfirmGuestSelection={handleConfirmGuestSelection}
      onConfirmSelection={handleConfirmSelection}
      labels={labels}
    />
  );
};
